import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { updateProfile } from '@shared/services/api';
import { isNative } from '@shared/utils/platform';

const ThemeContext = createContext(null);

export const THEME_COLORS = [
  { name: 'Black', value: '#000000' },
  { name: 'Charcoal', value: '#1C1C1E' },
  { name: 'Midnight', value: '#0F172A' },
  { name: 'Navy', value: '#1E3A5F' },
  { name: 'Purple', value: '#4A1D96' },
  { name: 'Pink', value: '#831843' },
  { name: 'Red', value: '#7F1D1D' },
  { name: 'Green', value: '#14532D' },
  { name: 'Teal', value: '#134E4A' },
  { name: 'Brown', value: '#422006' },
];

const STORAGE_KEY = 'ln-bg-color';

export function ThemeProvider({ children }) {
  const [bgColor, setBgColorState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || '#000000';
    } catch {
      return '#000000';
    }
  });

  const setBgColor = useCallback((color) => {
    setBgColorState(color);
    try {
      localStorage.setItem(STORAGE_KEY, color);
    } catch {}
    // Save to DB (fire and forget)
    updateProfile({ theme_color: color }).catch(() => {});
  }, []);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', bgColor);
    if (isNative) {
      import('@capacitor/status-bar').then(({ StatusBar }) => {
        StatusBar.setBackgroundColor({ color: bgColor }).catch(() => {});
      });
    }
  }, [bgColor]);

  // Sync from user data on mount (DB is source of truth)
  const syncFromUser = useCallback((user) => {
    if (user?.theme_color && user.theme_color !== bgColor) {
      setBgColorState(user.theme_color);
      try {
        localStorage.setItem(STORAGE_KEY, user.theme_color);
      } catch {}
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ bgColor, setBgColor, syncFromUser }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}
